import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { NotificationListItemDto } from "../models/notification/notification";
import {
    TransportOrderNotificationListItemDto,
    UnconfirmedNotificationStates,
} from "../models/transport/transportOrderNotification";
import { NotificationSource } from "../models/notification/notificationSource";
import { getDesktopNotifications } from "../services/notificationService";
import { fetchAllTransportOrderNotifications } from "../services/transportOrderService";
import { setUnconfirmedNotificationCount } from "./appSlice";

export interface NotificationState {
    desktopNotifications: NotificationListItemDto[];
    transportNotifications: TransportOrderNotificationListItemDto[];
    transportTotalCount: number;
    loading: boolean;
}

export const initialState: NotificationState = {
    desktopNotifications: [],
    transportNotifications: [],
    transportTotalCount: 0,
    loading: false,
};

export const fetchNotifications = createAsyncThunk(
    "notifications/fetchAll",
    async (
        {
            fetchDesktop,
            fetchTransport,
            transportCountOnly = false,
        }: { fetchDesktop: boolean; fetchTransport: boolean; transportCountOnly?: boolean },
        thunkApi
    ) => {
        let desktopNotifications: NotificationListItemDto[] = [];
        let transportNotifications: TransportOrderNotificationListItemDto[] =
            [];
        let transportTotalCount = 0;

        const promises: Promise<void>[] = [];

        if (fetchDesktop) {
            promises.push(
                getDesktopNotifications().then((result) => {
                    desktopNotifications = result ?? [];
                })
            );
        }

        if (fetchTransport) {
            promises.push(
                fetchAllTransportOrderNotifications({
                    source: NotificationSource.MobileToDesktop,
                    states: UnconfirmedNotificationStates,
                    pageSize: transportCountOnly ? 1 : 100, // Backend: Cap at 100 items per page
                }).then((result) => {
                    if (!transportCountOnly) {
                        transportNotifications = result?.items ?? [];
                    }
                    transportTotalCount = result?.totalCount ?? 0;
                })
            );
        }

        await Promise.all(promises);

        thunkApi.dispatch(
            setUnconfirmedNotificationCount(
                desktopNotifications.length + transportTotalCount
            )
        );

        return {
            desktopNotifications: fetchDesktop ? desktopNotifications : undefined,
            transportNotifications: fetchTransport && !transportCountOnly ? transportNotifications : undefined,
            transportTotalCount: fetchTransport ? transportTotalCount : undefined,
        };
    }
);

export const notificationSlice = createSlice({
    name: "notifications",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.desktopNotifications !== undefined) {
                    state.desktopNotifications =
                        action.payload.desktopNotifications;
                }
                if (action.payload.transportNotifications !== undefined) {
                    state.transportNotifications =
                        action.payload.transportNotifications;
                }
                if (action.payload.transportTotalCount !== undefined) {
                    state.transportTotalCount =
                        action.payload.transportTotalCount;
                }
            })
            .addCase(fetchNotifications.rejected, (state) => {
                state.loading = false;
            });
    },
});

export const notificationReducer = notificationSlice.reducer;
